import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFilesQuery, useUploadFileMutation } from '@platform/state/api';
import { Button, IconButton } from '@platform/ui';
import { formatBytes } from '@platform/core/lib/format';
import type { IControlProps } from '../IControlProps';
import { FilePreviewDialog } from '../file/FilePreviewDialog';
import { fileTile } from '../file/fileTile';
import { useFileActions } from '../file/useFileActions';

/** Найбільший розмір файлу поля (UIUX/field-types/file: 25 MB). */
const MAX_BYTES = 25 * 1024 * 1024;

/**
 * Файл (UIUX/field-types/file): документ або зображення запису — плитка типу, ім'я, «розмір · тип», перегляд (зображення, PDF),
 * завантаження, заміна й очищення; вибраний файл (до 25 MB) додається до файлів запису, а поле зберігає ідентифікатор вкладення.
 * Без запису (нова форма) файл можна додати після створення.
 */
export function FileField({ id, value, onChange, readOnly, record }: IControlProps<string>) {
  const { t } = useTranslation();
  const recordId = record?.id ?? '';
  const { data: files } = useFilesQuery(recordId, { skip: !recordId });
  const [upload, { isLoading }] = useUploadFileMutation();
  const actions = useFileActions();
  const [error, setError] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const file = files?.find((f) => f.id === value);
  const pick = async (f: File) => {
    if (f.size > MAX_BYTES) { setError(t('fieldControls.fileTooLarge', { max: '25 MB' })); return; }
    setError('');
    const att = await upload({ recordId, file: f }).unwrap().catch(() => null);
    if (att) onChange(att.id); else setError(t('fieldControls.uploadFailed'));
  };
  const tile = file ? fileTile(file.fileName, file.mimeType) : null;
  return (
    <div id={id} className="fc-file">
      <input ref={input} type="file" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void pick(f); e.target.value = ''; }} />
      {file && tile ? (
        <>
          <span className="fc-file__tile" style={{ background: tile.tone }}>{tile.ext}</span>
          <span className="fc-file__main">
            <span className="fc-file__name" title={file.fileName}>{file.fileName}</span>
            <span className="fc-file__meta">{formatBytes(file.sizeBytes)} · {tile.ext}</span>
          </span>
          {tile.previewable && <IconButton icon="eye" size="sm" variant="ghost" label={t('fieldControls.preview')} disabled={actions.busy} onClick={() => actions.open(file)} />}
          <IconButton icon="download" size="sm" variant="ghost" label={t('fieldControls.download')} disabled={actions.busy} onClick={() => actions.download(file)} />
          {!readOnly && <IconButton icon="refresh-cw" size="sm" variant="ghost" label={t('fieldControls.replace')} disabled={isLoading} onClick={() => input.current?.click()} />}
          {!readOnly && <IconButton icon="x" size="sm" variant="ghost" label={t('fieldControls.clear')} onClick={() => onChange(null)} />}
        </>
      ) : value ? (
        <>
          <span className="fc-file__tile" style={{ background: 'var(--solid-grey)' }}>FILE</span>
          <span className="fc-file__main"><span className="fc-file__name">{t('fieldControls.fileAttached')}</span></span>
          {!readOnly && recordId && <IconButton icon="refresh-cw" size="sm" variant="ghost" label={t('fieldControls.replace')} disabled={isLoading} onClick={() => input.current?.click()} />}
          {!readOnly && <IconButton icon="x" size="sm" variant="ghost" label={t('fieldControls.clear')} onClick={() => onChange(null)} />}
        </>
      ) : !readOnly && recordId ? (
        <Button size="sm" startIcon="paperclip" disabled={isLoading} onClick={() => input.current?.click()}>{isLoading ? t('fieldControls.uploading') : t('fieldControls.chooseFile')}</Button>
      ) : (
        <span className="fc-file__empty">{recordId || readOnly ? t('fieldControls.noFile') : t('fieldControls.fileAfterCreate')}</span>
      )}
      {(error || actions.failed) && <span className="field__error fc-file__error">{error || t('fieldControls.downloadFailed')}</span>}
      {actions.preview && <FilePreviewDialog preview={actions.preview} onClose={actions.close} />}
    </div>
  );
}
